import { useRouter } from 'next/router'
import { useCallback, useContext, useMemo, useState } from 'react'

import { AssetWithdrawalContext } from '@/components/base/AssetWithdrawal/context'
import WithdrawalTopTokensList from '@/components/base/AssetWithdrawal/WithdrawalTopTokensList'
import { TokenPickerRevampContext } from '@/components/base/TokenPickerRevamp/context'
import { WrappedTokensFlowContext } from '@/components/base/WrappedTokensFlow/context'
import DepositTopTokensList from '@/components/base/WrappedTokensFlow/DepositTopTokensList'
import { METHOD_TYPE } from '@/components/core-tokens-list/MethodSelectionDialog'
import useDepositTokenOptions from '@/hooks/use-deposit-token-options'
import { useSettings } from '@/hooks/use-settings'
import useTokens from '@/hooks/use-tokens'
import type { CustomTokenInfo } from '@/utils/helpers'

import { getTradeRoute } from '../list/helpers'
import { CURRENT_TYPE } from './helpers'
import View from './view'

const Container = () => {
  const router = useRouter()
  const { data: settingsData } = useSettings()
  const { fiatTokensList: tokenFiatOptions } = useTokens()

  const [currentType, setCurrentType] = useState('')
  const [currentMethod, setCurrentMethod] = useState('')
  const { setSelectedToken: setSelectedDepositToken } = useContext(WrappedTokensFlowContext)
  const { setSelectedToken: setSelectedWithdrawToken } = useContext(AssetWithdrawalContext)

  const { setIsOpenPicker } = useContext(TokenPickerRevampContext)
  const [isOpenMethodSelection, setIsOpenMethodSelection] = useState(false)

  const { tokenOptions: depositTokenOptions } = useDepositTokenOptions()

  const onClickType = useCallback((newType: string) => {
    setCurrentType(newType)
    setIsOpenMethodSelection(true)
  }, [])

  const onCloseMethodSelection = useCallback(() => {
    setCurrentType('')
    setIsOpenMethodSelection(false)
  }, [])

  const onSelectMethod = useCallback(
    (newMethod: string) => {
      setIsOpenPicker(true)
      setCurrentMethod(newMethod)
      setIsOpenMethodSelection(false)
    },
    [setIsOpenPicker],
  )

  const tokenOptions = useMemo(() => {
    if (currentMethod === METHOD_TYPE.bank) {
      return tokenFiatOptions
    }

    return depositTokenOptions
  }, [currentMethod, depositTokenOptions, tokenFiatOptions])

  const onPickAToken = useCallback(
    (token: CustomTokenInfo) => {
      if (currentType === CURRENT_TYPE.deposit) {
        if (currentMethod === METHOD_TYPE.bank) {
          router.push(`/buy/${token.symbol}`)
          return
        }
        if (currentMethod === METHOD_TYPE.network) {
          setSelectedDepositToken(token)
        }
        if (currentMethod === METHOD_TYPE.swapOthers) {
          router.push(getTradeRoute(token.symbol, true))
          return
        }
      }

      if (currentType === CURRENT_TYPE.withdraw) {
        if (currentMethod === METHOD_TYPE.bank) {
          router.push(`/sell/${token.symbol}`)
          return
        }
        if (currentMethod === METHOD_TYPE.network) {
          setSelectedWithdrawToken(token)
        }
      }
    },
    [router, currentType, currentMethod, setSelectedDepositToken, setSelectedWithdrawToken],
  )

  const topTokensPickerSlot = useMemo(() => {
    if (currentMethod === METHOD_TYPE.network) {
      if (currentType === CURRENT_TYPE.deposit) {
        return <DepositTopTokensList />
      }
      if (currentType === CURRENT_TYPE.withdraw) {
        return <WithdrawalTopTokensList />
      }
    }

    return null
  }, [currentType, currentMethod])

  const computedProps = {
    currentType,
    tokenOptions,
    onClickType,
    onPickAToken,
    topTokensPickerSlot,
    isOpenMethodSelection,
    onCloseMethodSelection,
    onSelectMethod,
    showEuro2024Banner: Boolean(settingsData?.nemo_swap_assets_euro2024_token_list_enabled),
  }

  return <View {...computedProps} />
}

export default Container
